import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  useTheme,
} from "@mui/material";
import { ErrorOutline, Home, ArrowBack, Search } from "@mui/icons-material";

const NotFound = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const handleGoHome = () => {
    navigate("/dashboard");
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "80vh",
          textAlign: "center",
          py: 4,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 6,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${theme.palette.primary.light}10 0%, ${theme.palette.primary.main}05 100%)`,
            border: `1px solid ${theme.palette.divider}`,
            width: "100%",
          }}
        >
          {/* 404 Icon */}
          <Box
            sx={{
              position: "relative",
              mb: 4,
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "120px", md: "180px" },
                fontWeight: 900,
                color: theme.palette.primary.main,
                opacity: 0.1,
                lineHeight: 1,
                userSelect: "none",
              }}
            >
              404
            </Typography>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              <ErrorOutline
                sx={{
                  fontSize: { xs: 80, md: 100 },
                  color: theme.palette.primary.main,
                }}
              />
            </Box>
          </Box>

          {/* Error Message */}
          <Typography
            variant="h3"
            gutterBottom
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: "2rem", md: "3rem" },
            }}
          >
            Page Not Found
          </Typography>

          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              mb: 4,
              maxWidth: 600,
              mx: "auto",
            }}
          >
            Oops! The page you're looking for doesn't exist. It might have been
            moved or deleted.
          </Typography>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              flexWrap: "wrap",
              mt: 4,
            }}
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<Home />}
              onClick={handleGoHome}
              disableElevation
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 600,
              }}
            >
              Go to Dashboard
            </Button>

            <Button
              variant="outlined"
              size="large"
              startIcon={<ArrowBack />}
              onClick={handleGoBack}
              disableElevation
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 600,
              }}
            >
              Go Back
            </Button>
          </Box>

          {/* Help Text */}
          <Box
            sx={{
              mt: 6,
              pt: 4,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Need help? Here are some useful links:
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 3,
                justifyContent: "center",
                mt: 2,
                flexWrap: "wrap",
              }}
            >
              <Button
                size="small"
                onClick={() => navigate("/dashboard/sites")}
                sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
              >
                My Sites
              </Button>
              <Button
                size="small"
                onClick={() => navigate("/dashboard/analytics")}
                sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
              >
                Analytics
              </Button>
              <Button
                size="small"
                onClick={() => navigate("/dashboard/settings")}
                sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
              >
                Settings
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Additional Info */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 4, display: "flex", alignItems: "center", gap: 0.5 }}
        >
          <Search fontSize="small" />
          Error Code: 404 - Page Not Found
        </Typography>
      </Box>
    </Container>
  );
};

export default NotFound;
